<template>
  <div class="container-fluid">
    <div class="row mb-3 align-items-center">
      <div class="col">
        <button class="btn btn-link pl-0" @click="goBack">← Volver</button>
        <h4 class="mb-1">{{ isEdit ? 'Editar cupón' : 'Alta de cupón' }}</h4>
        <p class="text-muted mb-0">
          Código de descuento para clientes. La vigencia y los límites se validan al canjear.
        </p>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="loading" class="text-center py-5 text-muted">Cargando…</div>

    <div v-else class="card">
      <div class="card-body">
        <form @submit.prevent="submit">
          <div class="row">
            <div class="col-md-6 form-group">
              <label>Código (ID cupón)</label>
              <input
                v-model.trim="form.code"
                class="form-control text-uppercase"
                required
                maxlength="40"
                :disabled="isEdit"
              />
            </div>
            <div class="col-md-3 form-group">
              <label>Fecha inicio</label>
              <input v-model="form.dateStart" type="date" class="form-control" required />
            </div>
            <div class="col-md-3 form-group">
              <label>Fecha expiración</label>
              <input v-model="form.dateExpiration" type="date" class="form-control" required />
            </div>
            <div class="col-md-6 form-group">
              <label>Tipo de descuento</label>
              <select v-model="form.discountType" class="form-control" required>
                <option value="porcentaje">Porcentaje (%)</option>
                <option value="cantidad">Cantidad (CLP)</option>
              </select>
            </div>
            <div class="col-md-6 form-group">
              <label>
                {{ form.discountType === 'cantidad' ? 'Cantidad (CLP)' : 'Porcentaje (%)' }}
              </label>
              <input
                v-model.number="form.amount"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4 form-group">
              <label>Compra mínima (CLP)</label>
              <input
                v-model.number="form.minimumPurchase"
                type="number"
                min="0"
                step="1"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4 form-group">
              <label>Veces por usuario</label>
              <input
                v-model.number="form.timePerUser"
                type="number"
                min="1"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4 form-group">
              <label>Máximo de cupones (0 = sin límite)</label>
              <input
                v-model.number="form.maximumCoupons"
                type="number"
                min="0"
                class="form-control"
                required
              />
            </div>
            <div v-if="isEdit" class="col-md-6 form-group">
              <label>Estado</label>
              <select v-model="form.status" class="form-control">
                <option value="active">Activo</option>
                <option value="disabled">Inactivo</option>
              </select>
            </div>
          </div>

          <div class="text-right mt-3">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="goBack">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Generar cupón' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { createCupon, fetchCupones, updateCupon } from '@/services/cuponesApi';

function toDateInput(isoOrMs) {
  if (!isoOrMs) return '';
  const d = new Date(isoOrMs);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDayIso(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function endOfDayIso(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

export default {
  name: 'CuponesFormPage',
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      form: {
        code: '',
        dateStart: '',
        dateExpiration: '',
        discountType: 'porcentaje',
        amount: 10,
        minimumPurchase: 0,
        timePerUser: 1,
        maximumCoupons: 0,
        status: 'active',
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
  },
  created() {
    if (this.isEdit) this.loadOne();
    else {
      const today = toDateInput(new Date());
      this.form.dateStart = today;
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'cupones' });
    },
    async loadOne() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const all = await fetchCupones();
        const row = all.find((c) => c.id === this.$route.params.id);
        if (!row) throw new Error('Cupón no encontrado');
        this.form = {
          code: row.code,
          dateStart: toDateInput(row.dateStart),
          dateExpiration: toDateInput(row.dateExpiration),
          discountType: row.discountType,
          amount: row.amount,
          minimumPurchase: row.minimumPurchase,
          timePerUser: row.timePerUser,
          maximumCoupons: row.maximumCoupons,
          status: row.status || 'active',
        };
      } catch (e) {
        this.errorMessage = e.message || 'Error al cargar';
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.errorMessage = '';
      this.saving = true;
      try {
        const body = {
          code: String(this.form.code || '').trim().toUpperCase(),
          amount: Number(this.form.amount),
          discountType: this.form.discountType,
          dateStart: startOfDayIso(this.form.dateStart),
          dateExpiration: endOfDayIso(this.form.dateExpiration),
          minimumPurchase: Number(this.form.minimumPurchase) || 0,
          timePerUser: Number(this.form.timePerUser) || 1,
          maximumCoupons: Number(this.form.maximumCoupons) || 0,
          status: this.form.status || 'active',
        };
        if (this.isEdit) {
          const { code, ...patch } = body;
          await updateCupon(this.$route.params.id, patch);
        } else {
          await createCupon(body);
        }
        this.$router.push({ name: 'cupones', query: { saved: '1' } });
      } catch (e) {
        this.errorMessage = e.message || 'No se pudo guardar el cupón';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
